import { useState } from 'react';

import EtatErreur from '../components/EtatErreur';
import Pills from '../components/Pills';
import Screen from '../components/Screen';
import Text from '../components/Text';
import EnteteApp from '../shell/EnteteApp';
import { useCoque } from '../shell/CoqueContext';
import FeuilleCommande from '../components/FeuilleCommande';
import { LignePersonne, PiedDeListe, LIGNES_PAR_MODULE } from './dashboard/briques';
import { useCommandes } from '../api/useCommandes';
import type { Commande, OngletCommandes } from '../api/useCommandes';
import { echecSansDonnee } from '../helpers/requete';
import { t } from '../i18n/langue';

/**
 * L'écran « Commandes » — #40, #87.
 *
 * ## En lecture seule, par ABSENCE D'OBJET
 *
 * Ce n'est pas une restriction imposée au mobile. Sur **453 transitions de
 * statut, 4 ont un auteur humain**, par une seule personne : le cycle Brouillon →
 * Validé → Terminée se déroule seul, et `use_order_alerts` est actif sur
 * **2 établissements sur 159**.
 *
 * L'écran répond à « qu'est-ce qui a été commandé », pas à « que dois-je faire ».
 *
 * ## Un seul filtre, et son défaut n'est pas neutre
 *
 * « À traiter » (Validé) / « Toutes », par défaut **À traiter** — parce que le web
 * filtre déjà sur Validé **sans le dire**. Le mobile fait la même chose en
 * l'annonçant, avec un témoin permanent : une liste filtrée qui ne le montre pas
 * est une liste qui ment.
 *
 * Écartés, mesures à l'appui : la recherche, les bornes de dates, le filtre de
 * source (`pms` et `manual` sont à **zéro ligne**), le tri et la pagination.
 *
 * ## Hors périmètre
 *
 * Les extras vendus via le PMS — **472/jour contre 2,18 commandes TF**, facteur
 * 200, et publics disjoints. Les mélanger noierait les commandes TF dans un flux
 * qui ne leur ressemble pas.
 */

const ONGLETS: OngletCommandes[] = ['a_traiter', 'toutes'];

/** Libellés lus au rendu : l'app remonte au changement de langue. */
function onglets(): { valeur: OngletCommandes; libelle: string }[] {
    return ONGLETS.map((valeur) => ({ valeur, libelle: t(`commandes.onglets.${valeur}`) }));
}

/** Persisté : on revient sur l'écran là où on l'avait laissé. */
const CLE_ONGLET = 'mobile_commandes_onglet';

export default function Commandes() {
    const { ouvrirPanneau } = useCoque();

    const [onglet, setOnglet] = useState<OngletCommandes>(() => {
        try {
            return localStorage.getItem(CLE_ONGLET) === 'toutes' ? 'toutes' : 'a_traiter';
        } catch {
            return 'a_traiter';
        }
    });

    const [ouverte, setOuverte] = useState<Commande | null>(null);
    const { data, isLoading, isError, refetch } = useCommandes(onglet);

    const choisir = (cle: OngletCommandes) => {
        setOnglet(cle);
        try {
            localStorage.setItem(CLE_ONGLET, cle);
        } catch {
            /* Stockage refusé : le choix vaut pour cette session. */
        }
    };

    const toutes = data?.data ?? [];
    const visibles = toutes.slice(0, LIGNES_PAR_MODULE * 4);
    // Le serveur ne rend qu'une page (per_page=20) : le total vient de `meta`, sinon
    // « + N autres » vaudrait toujours 0 (audit 2026-09-23).
    const total = data?.meta?.total ?? toutes.length;
    // Un refetch en échec garde `data` (react-query 3) : la liste reste, l'échec
    // ne se dit que sans aucune donnée (relecture I1).
    const echec = echecSansDonnee({ isError, data });

    return (
        <Screen entete={<EnteteApp titre={t('commandes.titre')} onOuvrirPanneau={ouvrirPanneau} />}>
            <div className="border-b border-border-color-light bg-white px-4 pb-3 pt-3">
                <Pills
                    libelle={t('commandes.filtrer')}
                    options={onglets()}
                    valeur={onglet}
                    onChange={choisir}
                />
            </div>

            <div className="px-4 py-3">
                {isLoading && (
                    <Text variante="petit" ton="attenue">
                        {t('commun.chargement')}
                    </Text>
                )}

                {echec && <EtatErreur onReessayer={() => void refetch()} />}

                {!isLoading && !echec && visibles.length === 0 && (
                    <Text variante="petit" ton="attenue" className="block leading-snug">
                        {onglet === 'a_traiter'
                            ? t('commandes.aucuneATraiter')
                            : t('commandes.aucune')}
                    </Text>
                )}

                <div className="flex flex-col divide-y divide-border-color-light">
                    {visibles.map((commande) => (
                        <LignePersonne
                            key={commande.id}
                            nom={commande.customer_name ?? t('commandes.clientInconnu')}
                            principal={commande.catalog_label ?? t('commandes.commande')}
                            secondaire={commande.guest_room ?? undefined}
                            onOuvrir={() => setOuverte(commande)}
                        />
                    ))}
                </div>

                <div className="mt-3">
                    <PiedDeListe caches={total - visibles.length} />
                </div>
            </div>

            <FeuilleCommande commande={ouverte} onFermer={() => setOuverte(null)} />
        </Screen>
    );
}
